<template>
	<div
		ref="scroller"
		class="flex-1 min-h-0 pt-3 overflow-y-auto"
		@scroll.passive="handleScroll"
	>
		<!-- Empty state -->
		<div
			v-if="notifications.length === 0"
			class="flex flex-col items-center justify-center px-4 py-16 text-center"
			data-test="notifications-empty-state"
		>
			<div
				class="flex items-center justify-center rounded-full size-12 bg-[#E6F4EC] mb-4"
			>
				<IconCheckCircle size="24" class="text-[#3C7D5A]" />
			</div>

			<h3 class="mb-2 text-sm font-medium">
				{{ emptyStateTitle }}
			</h3>

			<p class="text-xs text-grey-700">
				{{ emptyStateMessage }}
			</p>
		</div>

		<!-- Notifications list -->
		<div v-else data-test="notifications-list" class="px-2">
			<article
				v-for="notification in notifications"
				:key="notification.id"
				class="relative p-4 mb-2 overflow-hidden transition-colors rounded-lg group"
				:class="
					isUnread(notification)
						? 'bg-info-50 hover:bg-grey-50'
						: 'bg-white hover:bg-neutral-100/70'
				"
				@mouseenter="hoveredNotificationId = notification.id"
				@mouseleave="hoveredNotificationId = null"
			>
				<div
					v-if="isUnread(notification)"
					class="absolute top-0 left-0 w-1 h-full bg-info"
				/>

				<div class="flex items-start gap-4">
					<div class="relative shrink-0">
						<div
							class="flex items-center justify-center overflow-hidden rounded-full size-10 bg-grey-400"
						>
							<img
								v-if="notificationImageUrl(notification)"
								:src="notificationImageUrl(notification)"
								alt=""
								class="object-cover w-full h-full"
								data-test="notification-avatar-image"
							/>

							<Component
								v-else
								:is="notificationFallbackIcon(notification)"
								:class="
									notificationFallbackIcon(notification) ===
									'IconUserProfile'
										? 'text-grey-400 bg-white object-cover w-full h-full'
										: 'text-white size-6'
								"
								data-test="notification-avatar-fallback"
							/>
						</div>

						<!-- Notification type badge -->
						<div
							class="absolute flex items-center justify-center border border-white rounded-full -right-1 -bottom-1 size-5"
							:class="notificationBadgeClass(notification)"
							data-test="notification-badge"
						>
							<Component
								:is="notificationBadgeIcon(notification)"
								size="10"
								class="shrink-0"
								data-test="notification-badge-icon"
							/>
						</div>
					</div>

					<div class="flex-1 min-w-0 pr-4">
						<button
							type="button"
							class="w-full text-left"
							@click="$emit('notification-click', notification)"
						>
							<h4
								class="mb-1 text-sm font-medium text-black"
								data-test="notification-title"
							>
								{{ resolveContent(notification).title }}
							</h4>

							<p
								class="text-sm text-grey-800 mb-2"
								data-test="notification-message"
							>
								{{ resolveContent(notification).message }}
							</p>

							<p
								class="flex items-center text-xs text-grey-700 gap-x-2 mb-0"
								data-test="notification-legend"
							>
								<span
									v-if="resolveContent(notification).legend"
									class="block font-medium truncate max-w-44"
									data-test="notification-legend-text"
								>
									{{ resolveContent(notification).legend }}
								</span>
								<span
									v-if="resolveContent(notification).legend"
									class="block mx-1.5"
									data-test="notification-legend-separator"
								>
									•
								</span>
								<span
									class="block text-grey-600"
									data-test="notification-legend-time"
								>
									{{ notificationRelativeTime(notification) }}
								</span>
							</p>
						</button>
					</div>

					<!-- Quick actions -->
					<div
						class="absolute flex items-center gap-1 top-3.5 right-4 shrink-0"
						:class="
							showQuickActions(notification)
								? 'opacity-100'
								: 'opacity-0 group-hover:opacity-100'
						"
					>
						<!-- Mark as read -->
						<button
							v-if="isUnread(notification)"
							type="button"
							class="inline-flex items-center justify-center size-6 text-black rounded-md transition-colors duration-200 hover:bg-grey-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#457F98]"
							:aria-label="$t('notification.mark-as-read')"
							data-test="notification-action-read"
							@click.stop="$emit('mark-read', notification)"
						>
							<IconCheck size="16" />
						</button>

						<!-- Delete -->
						<button
							type="button"
							class="inline-flex items-center justify-center size-6 text-black rounded-md transition-colors duration-200 hover:bg-[#FEE4E2] hover:text-[#7F241D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#457F98]"
							:aria-label="$t('crud.delete')"
							data-test="notification-action-delete"
							@click.stop="
								$emit('delete-notification', notification)
							"
						>
							<IconTrash size="14" />
						</button>
					</div>
				</div>

				<div class="absolute bottom-0 left-0 w-full h-px bg-grey-200" />
			</article>

			<div
				v-if="isLoadingMore"
				class="px-4 py-4 text-xs text-center text-neutral-500"
				data-test="notifications-loading-more"
			>
				{{ $t('general.loading') }}
			</div>
		</div>
	</div>
</template>

<script>
import {
	IconLock,
	IconBuilding,
	IconCheck,
	IconCheckCircle,
	IconClipboard,
	IconTeamAlignment,
	IconFile,
	IconFileCheck,
	IconFileTimes,
	IconFilePlus,
	IconTimes,
	IconUsers,
	IconUserProfile,
	IconTrash
} from '~/components/Icons'
import Icon from '~/components/ui/common/Icon'

export default {
	name: 'NotificationsList',
	components: {
		IconLock,
		IconBuilding,
		IconCheck,
		IconCheckCircle,
		IconClipboard,
		IconTeamAlignment,
		IconFile,
		IconFileCheck,
		IconFileTimes,
		IconFilePlus,
		IconTimes,
		IconUsers,
		IconUserProfile,
		IconTrash,
		Icon
	},
	props: {
		notifications: {
			type: Array,
			default: () => []
		},
		isUnread: {
			type: Function,
			required: true
		},
		emptyStateMessage: {
			type: String,
			required: true
		},
		emptyStateTitle: {
			type: String,
			required: true
		},
		resolveContent: {
			type: Function,
			required: true
		},
		notificationRelativeTime: {
			type: Function,
			required: true
		},
		notificationImageUrl: {
			type: Function,
			required: true
		},
		notificationFallbackIcon: {
			type: Function,
			required: true
		},
		notificationBadgeIcon: {
			type: Function,
			required: true
		},
		notificationBadgeClass: {
			type: Function,
			required: true
		},
		isTouchDevice: {
			type: Boolean,
			default: false
		},
		hasMore: {
			type: Boolean,
			default: false
		},
		isLoadingMore: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			hoveredNotificationId: null
		}
	},
	mounted() {
		this.maybeLoadMoreIfNeeded()
	},
	updated() {
		this.maybeLoadMoreIfNeeded()
	},
	methods: {
		handleScroll() {
			const scroller = this.$refs.scroller

			if (!scroller || this.isLoadingMore || !this.hasMore) {
				return
			}

			const distanceToBottom =
				scroller.scrollHeight -
				(scroller.scrollTop + scroller.clientHeight)

			if (distanceToBottom <= 80) {
				this.$emit('load-more')
			}
		},
		maybeLoadMoreIfNeeded() {
			const scroller = this.$refs.scroller

			if (
				!scroller ||
				this.isLoadingMore ||
				!this.hasMore ||
				this.notifications.length === 0
			) {
				return
			}

			if (scroller.scrollHeight <= scroller.clientHeight + 8) {
				this.$emit('load-more')
			}
		},
		showQuickActions(notification) {
			return (
				this.isTouchDevice ||
				this.hoveredNotificationId === notification.id
			)
		}
	}
}
</script>
